import { useCallback, useSyncExternalStore } from "react";
import { getCookie, setCookie } from "~/lib/cookies";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

// Per-cookie-name listener sets so every component reading the same cookie
// re-renders when any one of them writes a new value — no context/store library needed.
const listeners = new Map<string, Set<() => void>>();

function subscribe(name: string, onChange: () => void): () => void {
  let set = listeners.get(name);
  if (!set) {
    set = new Set();
    listeners.set(name, set);
  }
  set.add(onChange);
  return () => set.delete(onChange);
}

function notify(name: string): void {
  listeners.get(name)?.forEach((listener) => listener());
}

export function useCookieState<T extends string>(name: string, defaultValue: T): [T, (value: T) => void] {
  const value = useSyncExternalStore(
    (onChange) => subscribe(name, onChange),
    () => (getCookie(name) as T | undefined) ?? defaultValue,
    // Server never has the cookie in this flow — always the default on first paint.
    () => defaultValue,
  );

  const setValue = useCallback(
    (next: T) => {
      setCookie(name, next, ONE_YEAR_SECONDS);
      notify(name);
    },
    [name],
  );

  return [value, setValue];
}

// useSyncExternalStore requires getSnapshot to return the same reference when nothing
// changed — cache the parsed object per cookie name, keyed on the raw string, so re-reads
// of an unchanged cookie don't return a fresh object (which would loop forever).
const jsonCache = new Map<string, { raw: string | undefined; parsed: unknown }>();

function readJson<T>(name: string, defaultValue: T): T {
  const raw = getCookie(name);
  const cached = jsonCache.get(name);
  if (cached && cached.raw === raw) {
    return cached.parsed as T;
  }

  let parsed = defaultValue;
  if (raw) {
    try {
      parsed = { ...defaultValue, ...JSON.parse(raw) };
    } catch {
      parsed = defaultValue;
    }
  }
  jsonCache.set(name, { raw, parsed });
  return parsed;
}

// Same subscribe/notify machinery as useCookieState, for a cookie holding a JSON object
// instead of a raw string. setValue merges rather than replaces, so updating one field
// can't clobber sibling fields written by another component reading the same cookie.
export function useJsonCookieState<T extends object>(name: string, defaultValue: T): [T, (value: Partial<T>) => void] {
  const value = useSyncExternalStore(
    (onChange) => subscribe(name, onChange),
    () => readJson(name, defaultValue),
    () => defaultValue,
  );

  const setValue = useCallback(
    (next: Partial<T>) => {
      setCookie(name, JSON.stringify({ ...readJson(name, defaultValue), ...next }), ONE_YEAR_SECONDS);
      notify(name);
    },
    [name, defaultValue],
  );

  return [value, setValue];
}
